/**
 * JourneyWindingPath — the seven journey areas rendered as a winding road.
 * Alternating left/right stops connected by S-curves; the family's current
 * area pulses with a "You are here" pill. Stops marked "been here" get a
 * check badge and color in the road segment they traveled. Tap any stop to
 * open its details. Optionally shareable as a PNG snapshot.
 */
import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const W = 640;
const STEP = 116;
const TOP = 64;
const LEFT_X = 208;
const RIGHT_X = 432;

function wrap(str, max = 18) {
    const words = String(str || "").split(/\s+/);
    const lines = [];
    let cur = "";
    for (const w of words) {
        const cand = cur ? `${cur} ${w}` : w;
        if (cand.length <= max) cur = cand;
        else { if (cur) lines.push(cur); cur = w; }
    }
    if (cur) lines.push(cur);
    return lines.slice(0, 2);
}

function segmentD(a, b) {
    return `M ${a.x} ${a.y} C ${a.x} ${a.y + STEP * 0.62}, ${b.x} ${b.y - STEP * 0.62}, ${b.x} ${b.y}`;
}

export default function JourneyWindingPath({ areas, primaryKey, alsoKeys = [], visitedKeys = [], pinsByArea = {}, onOpen, shareable = false }) {
    const wrapRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const pts = useMemo(
        () => areas.map((a, i) => ({ area: a, x: i % 2 === 0 ? LEFT_X : RIGHT_X, y: TOP + i * STEP })),
        [areas]
    );
    const H = TOP + (areas.length - 1) * STEP + 56;

    const roadD = useMemo(() => {
        if (!pts.length) return "";
        let d = `M ${pts[0].x} ${pts[0].y}`;
        for (let i = 1; i < pts.length; i++) {
            const a = pts[i - 1], b = pts[i];
            d += ` C ${a.x} ${a.y + STEP * 0.62}, ${b.x} ${b.y - STEP * 0.62}, ${b.x} ${b.y}`;
        }
        return d;
    }, [pts]);

    // A segment reads as "traveled" when the stop it departs FROM is marked
    // "been here" — the family moved on down the road from there.
    const traveledSegs = useMemo(
        () => pts.slice(0, -1)
            .map((p, i) => ({ d: segmentD(p, pts[i + 1]), color: p.area.color, key: p.area.key, traveled: visitedKeys.includes(p.area.key) }))
            .filter(s => s.traveled),
        [pts, visitedKeys]
    );

    const makePngBlob = async () => {
        const svg = wrapRef.current?.querySelector("svg");
        if (!svg) return null;
        const clone = svg.cloneNode(true);
        clone.setAttribute("width", String(W * 2));
        clone.setAttribute("height", String(H * 2));
        const xml = new XMLSerializer().serializeToString(clone);
        const img = new Image();
        await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = rej;
            img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
        });
        const HEAD = 130;
        const canvas = document.createElement("canvas");
        canvas.width = W * 2;
        canvas.height = H * 2 + HEAD;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FDFBF4";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#1F2624";
        ctx.font = "600 44px Georgia, 'Times New Roman', serif";
        ctx.fillText("Our family's road", 48, 66);
        ctx.fillStyle = "#6E7A74";
        ctx.font = "24px Arial, sans-serif";
        const dateStr = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
        ctx.fillText(`Memory Lane Care Navigator · ${dateStr}`, 48, 102);
        ctx.drawImage(img, 0, HEAD, W * 2, H * 2);
        return new Promise(res => canvas.toBlob(res, "image/png"));
    };

    const downloadSnapshot = async () => {
        setBusy(true);
        try {
            const blob = await makePngBlob();
            if (!blob) throw new Error("no svg");
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "our-familys-road.png";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Snapshot downloaded.");
        } catch {
            toast.error("Couldn't create the image. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const shareSnapshot = async () => {
        setBusy(true);
        try {
            const blob = await makePngBlob();
            if (!blob) throw new Error("no svg");
            const file = new File([blob], "our-familys-road.png", { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: "Our family's road", text: "Here's where things stand on our dementia care journey — from Memory Lane." });
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "our-familys-road.png";
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Image downloaded — attach it to a text or email to share.");
            }
        } catch (e) {
            if (e?.name !== "AbortError") toast.error("Couldn't share the image. Try Download instead.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-4" data-testid="roadmap-winding-path" ref={wrapRef}>
            <style>{`@keyframes mlPathPulse { 0%,100% { r: 20px; opacity: .3; } 50% { r: 30px; opacity: .06; } } .ml-path-pulse { animation: mlPathPulse 2.2s ease-in-out infinite; }`}</style>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 px-1 text-[11px] text-[#4C5551]" data-testid="winding-legend">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-teal inline-block animate-pulse" /> You are here</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-white border border-teal text-teal-dark inline-flex items-center justify-center text-[9px] font-bold">✓</span> Been here</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border border-dashed border-[#4C5551] inline-block" /> Also dealing with</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#F3EDFC] border border-[#8B5CF6] inline-block" /> Change pins</span>
            </div>
            <div
                className="relative rounded-3xl border border-cream-dark overflow-hidden px-1 py-2 md:px-3 md:py-4"
                style={{ background: "linear-gradient(165deg, #FBF7EC 0%, #F3F4E7 50%, #EAF2EE 100%)" }}
            >
                <div aria-hidden="true" className="absolute -left-16 top-12 w-56 h-56 rounded-full bg-teal-soft/50 blur-3xl pointer-events-none" />
                <div aria-hidden="true" className="absolute -right-16 bottom-20 w-64 h-64 rounded-full bg-[#F4E3C8]/60 blur-3xl pointer-events-none" />
                <div aria-hidden="true" className="absolute left-1/3 top-1/2 w-48 h-48 rounded-full bg-[#E9F0E7]/70 blur-3xl pointer-events-none" />
                <svg viewBox={`0 0 ${W} ${H}`} className="relative w-full h-auto" role="img"
                    aria-label="Your journey — the seven areas rendered as a winding road; tap any stop for details">
                <path d={roadD} fill="none" stroke="#F1E3AC" strokeWidth="30" strokeLinecap="round" />
                {traveledSegs.map(seg => (
                    <path key={`traveled-${seg.key}`} d={seg.d} fill="none" stroke={seg.color} strokeOpacity="0.35"
                        strokeWidth="30" strokeLinecap="round" data-testid={`winding-traveled-${seg.key}`} />
                ))}
                <motion.path d={roadD} fill="none" stroke="#fff" strokeWidth="2.5" strokeDasharray="7 11" strokeLinecap="round"
                    initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4, ease: "easeOut" }} />
                <text x={pts[0].x} y={pts[0].y - 34} textAnchor="middle" fontSize="9" fill="#6E7A74" letterSpacing="1.5" fontWeight="600">
                    THE ROAD BEGINS
                </text>

                {pts.map(({ area, x, y }, i) => {
                    const isPrimary = area.key === primaryKey;
                    const isAlso = alsoKeys.includes(area.key);
                    const isVisited = visitedKeys.includes(area.key);
                    const pinCount = (pinsByArea[area.key] || []).length;
                    const labelSide = x === LEFT_X ? "left" : "right";
                    const tx = labelSide === "left" ? x - 32 : x + 32;
                    const anchor = labelSide === "left" ? "end" : "start";
                    const lines = wrap(area.title);
                    const hitX = labelSide === "left" ? x - 200 : x - 28;
                    return (
                        <motion.g key={area.key}
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 + i * 0.09, duration: 0.4 }}
                            onClick={() => onOpen(area.key)}
                            style={{ cursor: "pointer" }}
                            data-testid={`winding-stop-${area.key}`}
                        >
                            <title>{`${area.title} — tap for details`}</title>
                            <rect x={hitX} y={y - 30} width="228" height="60" fill="transparent" />
                            {isPrimary && (
                                <circle cx={x} cy={y} r="20" opacity="0.25" fill={area.color} className="ml-path-pulse" />
                            )}
                            {isAlso && (
                                <circle cx={x} cy={y} r="24" fill="none" stroke={area.color} strokeWidth="1.5" strokeDasharray="3 4" />
                            )}
                            <circle cx={x} cy={y} r={isPrimary ? 17 : 14} fill={area.color} stroke="#fff" strokeWidth="3" />
                            <text x={x} y={y + 4.5} textAnchor="middle" fontSize={isPrimary ? 13 : 12} fontWeight="700" fill="#fff">{i + 1}</text>

                            {isVisited && (
                                <g data-testid={`winding-visited-${area.key}`}>
                                    <circle cx={x + 14} cy={y + 14} r="8" fill="#fff" stroke={area.color} strokeWidth="1.5" />
                                    <text x={x + 14} y={y + 17.5} textAnchor="middle" fontSize="10" fontWeight="800" fill={area.color}>✓</text>
                                </g>
                            )}

                            {isPrimary && (
                                <g data-testid={`winding-you-are-here-${area.key}`}>
                                    <rect x={x - 44} y={y - 46} width="88" height="19" rx="9.5" fill={area.color} />
                                    <text x={x} y={y - 33} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff" letterSpacing="1">YOU ARE HERE</text>
                                </g>
                            )}

                            {lines.map((ln, li) => (
                                <text key={li} x={tx} y={y + 5 - (lines.length - 1) * 8 + li * 15} textAnchor={anchor}
                                    fontSize="13.5" fontWeight={isPrimary ? 700 : 600}
                                    fill={isPrimary ? area.color : "#1F2624"}>{ln}</text>
                            ))}
                            <text x={tx} y={y + 5 + (lines.length - 1) * 15 + 14} textAnchor={anchor} fontSize="9.5" fill="#6E7A74">
                                {isVisited ? "Been here ✓ · " : isAlso ? "Also dealing with · " : ""}Details →
                            </text>

                            {pinCount > 0 && (
                                <g data-testid={`winding-pins-${area.key}`}>
                                    <circle cx={x + 17} cy={y - 15} r="8" fill="#F3EDFC" stroke="#8B5CF6" strokeWidth="1" />
                                    <text x={x + 17} y={y - 12} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#6D4BC0">{pinCount}</text>
                                </g>
                            )}
                        </motion.g>
                    );
                })}
                <text x={pts[pts.length - 1].x} y={pts[pts.length - 1].y + 40} textAnchor="middle" fontSize="9" fill="#6E7A74" letterSpacing="1.5" fontWeight="600">
                    THE ROAD CONTINUES — SUPPORT BESIDE YOU
                </text>
            </svg>
            </div>
            <p className="mt-1.5 text-[11px] text-[#6E7A74] text-center">
                Tap any stop to open what it means, what to watch for, and which services usually help there — and to mark it &ldquo;been here&rdquo;.
            </p>
            {shareable && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-testid="winding-share-bar">
                    <button type="button" onClick={downloadSnapshot} disabled={busy}
                        data-testid="winding-share-download"
                        className="inline-flex items-center gap-1.5 rounded-2xl border border-cream-dark bg-white px-4 py-2 min-h-[44px] text-xs font-semibold text-[#1F2624] hover:border-teal-dark hover:text-teal-dark transition-colors disabled:opacity-50">
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} strokeWidth={2} />} Download image
                    </button>
                    <button type="button" onClick={shareSnapshot} disabled={busy}
                        data-testid="winding-share-native"
                        className="inline-flex items-center gap-1.5 rounded-2xl bg-teal text-white px-4 py-2 min-h-[44px] text-xs font-semibold hover:bg-teal-dark transition-colors disabled:opacity-50">
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} strokeWidth={2} />} Share snapshot
                    </button>
                </div>
            )}
        </div>
    );
}
